import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { useData } from '../../contexts/DataContext';
import { useQrScanner } from '../../hooks/useQrScanner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Monitor, Plus, Trash2, RefreshCw, Shield, Link2, Copy, CheckCircle, XCircle,
  Clock, Edit, Layers, Eye, Settings, MessageSquare, Wrench, Ticket,
  BarChart3, Calendar, Users, Camera, QrCode
} from 'lucide-react';

const SLIDE_ICONS = {
  equipment_status: Wrench,
  active_tickets: Ticket,
  kpis: BarChart3,
  revisions: Calendar,
  shifts_on_duty: Users,
  low_stock: Settings,
  ticket_trend: Eye,
  recent_movements: Settings,
  preventive_maintenance: Shield,
  iscir_expiring: Clock,
  top_problematic: Monitor,
  tech_productivity: CheckCircle,
  lubrication_overdue: Settings,
  daily_summary: Layers,
  custom_message: MessageSquare
};

const SLIDE_LABELS = {
  equipment_status: 'Status Echipamente',
  active_tickets: 'Tichete Active',
  kpis: 'KPI-uri Mentenanta',
  revisions: 'Revizii Planificate',
  shifts_on_duty: 'Tehnicieni pe Schimb',
  low_stock: 'Articole Sub Stoc Minim',
  ticket_trend: 'Grafic Trend Tichete',
  recent_movements: 'Miscari Stoc Recente',
  preventive_maintenance: 'Mentenanta Preventiva',
  iscir_expiring: 'ISCIR Expirari',
  top_problematic: 'Top Echipamente Problematice',
  tech_productivity: 'Productivitate Tehnicieni',
  lubrication_overdue: 'Lubrifiere Depasiri',
  daily_summary: 'Rezumat Zilnic',
  custom_message: 'Mesaj Custom'
};

const DEFAULT_SLIDES = [
  { type: 'equipment_status', enabled: true, title: '', message: '' },
  { type: 'active_tickets', enabled: true, title: '', message: '' },
  { type: 'kpis', enabled: true, title: '', message: '' },
  { type: 'revisions', enabled: true, title: '', message: '' },
  { type: 'shifts_on_duty', enabled: true, title: '', message: '' },
  { type: 'low_stock', enabled: false, title: '', message: '' },
  { type: 'ticket_trend', enabled: false, title: '', message: '' },
  { type: 'recent_movements', enabled: false, title: '', message: '' },
  { type: 'preventive_maintenance', enabled: false, title: '', message: '' },
  { type: 'iscir_expiring', enabled: false, title: '', message: '' },
  { type: 'top_problematic', enabled: false, title: '', message: '' },
  { type: 'tech_productivity', enabled: false, title: '', message: '' },
  { type: 'lubrication_overdue', enabled: false, title: '', message: '' },
  { type: 'daily_summary', enabled: false, title: '', message: '' },
];

const KioskDevicesPage = () => {
  const { plants } = useData();
  const [activeTab, setActiveTab] = useState('devices');
  const [devices, setDevices] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Link dialog
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkForm, setLinkForm] = useState({ device_name: '', plant_id: '', profile_id: '' });
  const [generatedLink, setGeneratedLink] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Profile dialog
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({
    name: '', description: '', slides: [...DEFAULT_SLIDES], slide_interval: 20, plant_id: ''
  });

  // Device edit dialog
  const [showDeviceEditDialog, setShowDeviceEditDialog] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [deviceEditForm, setDeviceEditForm] = useState({ device_name: '', profile_id: '', plant_id: '' });

  // Approve dialog
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [approvingDevice, setApprovingDevice] = useState(null);
  const [approveForm, setApproveForm] = useState({ device_name: '', plant_id: '', profile_id: '' });
  const [approving, setApproving] = useState(false);

  // QR Scanner
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const { start: startQrKiosk, stop: stopQrKiosk } = useQrScanner();
  const qrReaderRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [devRes, profRes] = await Promise.all([
        api.get('/kiosk/devices'),
        api.get('/kiosk/profiles')
      ]);
      setDevices(devRes.data);
      setProfiles(profRes.data);
    } catch {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Device Actions ────────────────────────────
  const handleRevoke = async (deviceId, name) => {
    if (!window.confirm(`Stergeti dispozitivul "${name}"? Acesta va pierde accesul la kiosk.`)) return;
    try {
      await api.delete(`/kiosk/devices/${deviceId}`);
      toast.success('Dispozitiv sters');
      fetchAll();
    } catch { toast.error('Eroare la stergere'); }
  };

  const openDeviceEdit = (device) => {
    setEditingDevice(device);
    setDeviceEditForm({
      device_name: device.device_name || '',
      profile_id: device.profile_id || '',
      plant_id: device.plant_id || ''
    });
    setShowDeviceEditDialog(true);
  };

  const handleDeviceUpdate = async () => {
    try {
      await api.patch(`/kiosk/devices/${editingDevice.id}`, deviceEditForm);
      toast.success('Dispozitiv actualizat');
      setShowDeviceEditDialog(false);
      fetchAll();
    } catch { toast.error('Eroare la actualizare'); }
  };

  const handleGenerateLink = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/kiosk/generate-link', {
        device_code: '',
        device_name: linkForm.device_name,
        plant_id: linkForm.plant_id,
        profile_id: linkForm.profile_id
      });
      setGeneratedLink(`${window.location.origin}/kiosk?token=${res.data.device_token}`);
      toast.success('Link generat!');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare'); }
    finally { setGenerating(false); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiat!');
  };

  // ── Approve Device ─────────────────────────────
  const openApproveDialog = (device) => {
    setApprovingDevice(device);
    setApproveForm({
      device_name: device.device_name || '',
      plant_id: '',
      profile_id: ''
    });
    setShowApproveDialog(true);
  };

  const handleApproveDevice = async () => {
    if (!approvingDevice) return;
    setApproving(true);
    try {
      await api.post('/kiosk/authorize-device', {
        device_code: approvingDevice.device_code,
        device_name: approveForm.device_name,
        plant_id: approveForm.plant_id,
        profile_id: approveForm.profile_id
      });
      toast.success('Dispozitiv autorizat cu succes!');
      setShowApproveDialog(false);
      setApprovingDevice(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la autorizare');
    } finally {
      setApproving(false);
    }
  };

  // ── QR Scanner ─────────────────────────────────
  const startQRScanner = async () => {
    setShowQRScanner(true);
    setManualCode('');
    setTimeout(async () => {
      try {
        setScanning(true);
        await startQrKiosk('kiosk-qr-reader', (decodedText) => {
          handleQRResult(decodedText);
          stopQRScanner();
        }, { fps: 10, qrbox: { width: 250, height: 250 } });
      } catch (err) {
        console.error('Camera QR error:', err);
        toast.error('Nu s-a putut porni camera. Folositi codul manual.');
        setScanning(false);
      }
    }, 300);
  };

  const stopQRScanner = async () => {
    await stopQrKiosk();
    setScanning(false);
  };

  const handleQRResult = async (text) => {
    // The QR code could be the device code directly, or a URL containing it
    let code = text.trim();
    // If it's a URL like /kiosk/authorize/ABCDEF, extract the code
    const match = code.match(/authorize\/([A-Fa-f0-9]+)/);
    if (match) code = match[1];
    // Also handle just the hex code
    code = code.toUpperCase();
    await lookupDeviceByCode(code);
  };

  const lookupDeviceByCode = async (code) => {
    if (!code || code.length < 4) {
      toast.error('Cod invalid');
      return;
    }
    try {
      const res = await api.get(`/kiosk/device-info/${code}`);
      const device = res.data;
      if (device.status === 'approved') {
        toast.info('Acest dispozitiv este deja autorizat.');
        return;
      }
      setShowQRScanner(false);
      openApproveDialog(device);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Dispozitiv negasit cu acest cod');
    }
  };

  const handleCloseQRScanner = () => {
    stopQRScanner();
    setShowQRScanner(false);
    setManualCode('');
  };

  // ── Profile Actions ───────────────────────────
  const openNewProfile = () => {
    setEditingProfile(null);
    setProfileForm({ name: '', description: '', slides: [...DEFAULT_SLIDES], slide_interval: 20, plant_id: '' });
    setShowProfileDialog(true);
  };

  const openEditProfile = (p) => {
    setEditingProfile(p);
    // Ensure all slide types exist in form
    const existingTypes = (p.slides || []).map(s => s.type);
    const slides = [...(p.slides || [])];
    Object.keys(SLIDE_LABELS).forEach(type => {
      if (!existingTypes.includes(type)) {
        slides.push({ type, enabled: false, title: '', message: '' });
      }
    });
    setProfileForm({
      name: p.name, description: p.description || '',
      slides, slide_interval: p.slide_interval || 20, plant_id: p.plant_id || ''
    });
    setShowProfileDialog(true);
  };

  const handleSaveProfile = async () => {
    try {
      const payload = {
        ...profileForm,
        slides: profileForm.slides.filter(s => s.enabled || s.type === 'custom_message')
      };
      if (editingProfile) {
        await api.put(`/kiosk/profiles/${editingProfile.id}`, payload);
        toast.success('Profil actualizat');
      } else {
        await api.post('/kiosk/profiles', payload);
        toast.success('Profil creat');
      }
      setShowProfileDialog(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare'); }
  };

  const handleDeleteProfile = async (id, name) => {
    if (!window.confirm(`Stergeti profilul "${name}"?`)) return;
    try {
      await api.delete(`/kiosk/profiles/${id}`);
      toast.success('Profil sters');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare'); }
  };

  const toggleSlide = (idx) => {
    setProfileForm(prev => {
      const slides = [...prev.slides];
      slides[idx] = { ...slides[idx], enabled: !slides[idx].enabled };
      return { ...prev, slides };
    });
  };

  const updateSlideField = (idx, field, value) => {
    setProfileForm(prev => {
      const slides = [...prev.slides];
      slides[idx] = { ...slides[idx], [field]: value };
      return { ...prev, slides };
    });
  };

  const addCustomMessage = () => {
    setProfileForm(prev => ({
      ...prev,
      slides: [...prev.slides, { type: 'custom_message', enabled: true, title: 'Anunt', message: '' }]
    }));
  };

  const removeSlide = (idx) => {
    setProfileForm(prev => ({
      ...prev,
      slides: prev.slides.filter((_, i) => i !== idx)
    }));
  };

  const statusBadge = (status) => {
    switch (status) {
      case 'approved': return <Badge className="bg-green-100 text-green-700 hover:bg-green-100"><CheckCircle className="w-3 h-3 mr-1" /> Activ</Badge>;
      case 'pending': return <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100"><Clock className="w-3 h-3 mr-1" /> In asteptare</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const devicesForProfile = (profileId) => devices.filter(d => d.profile_id === profileId && d.status === 'approved').length;

  return (
    <div className="space-y-6" data-testid="kiosk-devices-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Kiosk Management</h1>
          <p className="text-slate-500 text-sm mt-1">Profile personalizate, dispozitive TV si linkuri de acces</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAll}><RefreshCw className="w-4 h-4 mr-1" /> Actualizeaza</Button>
          <Button variant="outline" size="sm" onClick={startQRScanner} className="text-green-600 border-green-200 hover:bg-green-50" data-testid="scan-qr-approve-btn">
            <Camera className="w-4 h-4 mr-1" /> Scaneaza QR
          </Button>
          <Button size="sm" onClick={() => {
            setLinkForm({ device_name: '', plant_id: '', profile_id: '' });
            setGeneratedLink(null);
            setShowLinkDialog(true);
          }} className="bg-[#0052CC] hover:bg-[#0041A3]" data-testid="generate-link-btn">
            <Link2 className="w-4 h-4 mr-1" /> Genereaza Link
          </Button>
        </div>
      </div>

      {/* Info */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="py-3 px-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Flux de lucru:</p>
              <ol className="text-xs text-blue-700 space-y-0.5 list-decimal list-inside">
                <li>Creati un <strong>Profil Kiosk</strong> cu slide-urile dorite (tab Profile)</li>
                <li>Cand un TV solicita acces, selectati profilul la autorizare</li>
                <li>Alternativ, generati un <strong>Link Direct</strong> cu profilul inclus</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="devices" data-testid="tab-devices"><Monitor className="w-4 h-4 mr-1" /> Dispozitive ({devices.filter(d => d.status !== 'revoked').length})</TabsTrigger>
          <TabsTrigger value="profiles" data-testid="tab-profiles"><Layers className="w-4 h-4 mr-1" /> Profile Kiosk ({profiles.length})</TabsTrigger>
        </TabsList>

        {/* ── DEVICES TAB ──────────────────── */}
        <TabsContent value="devices">
          <Card>
            <CardContent className="pt-4">
              {loading ? (
                <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div>
              ) : devices.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Monitor className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>Niciun dispozitiv inregistrat</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {devices.map(device => (
                    <div key={device.id} className={`flex items-center gap-4 p-4 rounded-lg border ${
                      device.status === 'approved' ? 'border-green-200 bg-green-50/50' : 'border-yellow-200 bg-yellow-50/50'
                    }`} data-testid={`kiosk-device-${device.id}`}>
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        device.status === 'approved' ? 'bg-green-100' : 'bg-yellow-100'
                      }`}>
                        <Monitor className={`w-5 h-5 ${
                          device.status === 'approved' ? 'text-green-600' : 'text-yellow-600'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-slate-900">{device.device_name}</p>
                          {statusBadge(device.status)}
                          {device.profile_name && (
                            <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                              <Layers className="w-2.5 h-2.5 mr-0.5" /> {device.profile_name}
                            </Badge>
                          )}
                          {device.created_via === 'link' && <Badge variant="outline" className="text-[10px]">Link</Badge>}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Cod: <span className="font-mono font-medium">{device.device_code}</span>
                          {device.approved_by && <span> | {device.approved_by}</span>}
                        </p>
                        <p className="text-xs text-slate-400">
                          {device.last_seen && <span>Ultima activitate: {new Date(device.last_seen).toLocaleString('ro-RO')}</span>}
                        </p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {device.status === 'pending' && (
                          <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => openApproveDialog(device)} data-testid={`approve-device-${device.id}`}>
                            <CheckCircle className="w-4 h-4 mr-1" /> Aproba
                          </Button>
                        )}
                        {device.status === 'approved' && (
                          <Button variant="ghost" size="sm" className="text-blue-500 hover:text-blue-700" onClick={() => openDeviceEdit(device)} data-testid={`edit-device-${device.id}`}>
                            <Settings className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleRevoke(device.id, device.device_name)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── PROFILES TAB ─────────────────── */}
        <TabsContent value="profiles">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div />
              <div className="flex gap-2">
                {profiles.length < 10 && (
                  <Button variant="outline" size="sm" onClick={async () => {
                    try {
                      const res = await api.post('/kiosk/profiles/seed');
                      toast.success(res.data.message);
                      fetchAll();
                    } catch (err) { toast.error(err.response?.data?.detail || 'Eroare'); }
                  }} data-testid="seed-profiles-btn">
                    <Layers className="w-4 h-4 mr-1" /> Genereaza Profile Default
                  </Button>
                )}
                <Button size="sm" onClick={openNewProfile} data-testid="new-profile-btn">
                  <Plus className="w-4 h-4 mr-1" /> Profil Nou
                </Button>
              </div>
            </div>
            {profiles.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-slate-400">
                  <Layers className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>Niciun profil creat</p>
                  <p className="text-xs mt-1">Creati un profil pentru a personaliza ce vede fiecare TV</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {profiles.map(p => {
                  const enabledSlides = (p.slides || []).filter(s => s.enabled);
                  return (
                    <Card key={p.id} className="hover:shadow-md transition-shadow" data-testid={`profile-${p.id}`}>
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-base">{p.name}</CardTitle>
                            {p.description && <p className="text-xs text-slate-500 mt-0.5">{p.description}</p>}
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditProfile(p)}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteProfile(p.id, p.name)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {enabledSlides.map((s, i) => {
                            const Icon = SLIDE_ICONS[s.type] || Eye;
                            return (
                              <Badge key={i} variant="outline" className="text-[10px] gap-1">
                                <Icon className="w-3 h-3" />
                                {s.title || SLIDE_LABELS[s.type] || s.type}
                              </Badge>
                            );
                          })}
                        </div>
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>{enabledSlides.length} slide-uri | {p.slide_interval || 20}s interval</span>
                          <span>{devicesForProfile(p.id)} TV-uri active</span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* ── PROFILE DIALOG ──────────────── */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5" /> {editingProfile ? 'Editeaza Profil' : 'Profil Kiosk Nou'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nume Profil *</Label>
                <Input value={profileForm.name} onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))} placeholder="ex: Hala Productie" data-testid="profile-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Interval Slide (secunde)</Label>
                <Input type="number" min="5" max="120" value={profileForm.slide_interval} onChange={e => setProfileForm(p => ({ ...p, slide_interval: parseInt(e.target.value) || 20 }))} data-testid="profile-interval-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Descriere</Label>
              <Input value={profileForm.description} onChange={e => setProfileForm(p => ({ ...p, description: e.target.value }))} placeholder="Scurta descriere" />
            </div>
            <div className="space-y-2">
              <Label>Fabrica (filtru date)</Label>
              <Select value={profileForm.plant_id || 'all'} onValueChange={v => setProfileForm(p => ({ ...p, plant_id: v === 'all' ? '' : v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate fabricile</SelectItem>
                  {(plants || []).map(pl => <SelectItem key={pl.id} value={pl.id}>{pl.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Slides Config */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-semibold">Slide-uri afisate</Label>
                <Button variant="outline" size="sm" onClick={addCustomMessage} data-testid="add-custom-slide-btn">
                  <MessageSquare className="w-3.5 h-3.5 mr-1" /> Adauga Mesaj
                </Button>
              </div>
              <div className="space-y-2">
                {profileForm.slides.map((slide, idx) => {
                  const Icon = SLIDE_ICONS[slide.type] || Eye;
                  const isCustom = slide.type === 'custom_message';
                  return (
                    <div key={idx} className={`border rounded-lg p-3 transition-colors ${slide.enabled ? 'bg-white border-blue-200' : 'bg-slate-50 border-slate-200'}`}>
                      <div className="flex items-center gap-3">
                        <Switch checked={slide.enabled} onCheckedChange={() => toggleSlide(idx)} data-testid={`slide-toggle-${idx}`} />
                        <Icon className={`w-4 h-4 flex-shrink-0 ${slide.enabled ? 'text-blue-500' : 'text-slate-400'}`} />
                        <div className="flex-1 min-w-0">
                          {isCustom ? (
                            <Input
                              value={slide.title}
                              onChange={e => updateSlideField(idx, 'title', e.target.value)}
                              placeholder="Titlu mesaj"
                              className="h-7 text-sm"
                              data-testid={`slide-title-${idx}`}
                            />
                          ) : (
                            <p className={`text-sm font-medium ${slide.enabled ? 'text-slate-900' : 'text-slate-400'}`}>
                              {SLIDE_LABELS[slide.type]}
                            </p>
                          )}
                        </div>
                        {isCustom && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => removeSlide(idx)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                      {isCustom && slide.enabled && (
                        <div className="mt-2 ml-10">
                          <textarea
                            value={slide.message}
                            onChange={e => updateSlideField(idx, 'message', e.target.value)}
                            placeholder="Continutul mesajului afisat pe ecran..."
                            className="w-full border rounded-md p-2 text-sm min-h-[60px] resize-y focus:outline-none focus:ring-2 focus:ring-blue-500"
                            data-testid={`slide-message-${idx}`}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProfileDialog(false)}>Anuleaza</Button>
            <Button onClick={handleSaveProfile} disabled={!profileForm.name || !profileForm.slides.some(s => s.enabled)} data-testid="save-profile-btn">
              {editingProfile ? 'Salveaza' : 'Creeaza Profil'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── DEVICE EDIT DIALOG ──────────── */}
      <Dialog open={showDeviceEditDialog} onOpenChange={setShowDeviceEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" /> Setari Dispozitiv
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nume Dispozitiv</Label>
              <Input value={deviceEditForm.device_name} onChange={e => setDeviceEditForm(p => ({ ...p, device_name: e.target.value }))} data-testid="edit-device-name" />
            </div>
            <div className="space-y-2">
              <Label>Profil Kiosk</Label>
              <Select value={deviceEditForm.profile_id || 'none'} onValueChange={v => setDeviceEditForm(p => ({ ...p, profile_id: v === 'none' ? '' : v }))}>
                <SelectTrigger data-testid="edit-device-profile">
                  <SelectValue placeholder="Fara profil (afiseaza tot)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Fara profil (afiseaza tot)</SelectItem>
                  {profiles.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">Schimba ce vede acest TV fara a-l revoca</p>
            </div>
            <div className="space-y-2">
              <Label>Fabrica</Label>
              <Select value={deviceEditForm.plant_id || 'all'} onValueChange={v => setDeviceEditForm(p => ({ ...p, plant_id: v === 'all' ? '' : v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate fabricile</SelectItem>
                  {(plants || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeviceEditDialog(false)}>Anuleaza</Button>
            <Button onClick={handleDeviceUpdate} data-testid="save-device-edit-btn">Salveaza</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── GENERATE LINK DIALOG ────────── */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Link2 className="w-5 h-5" /> Genereaza Link Acces</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!generatedLink ? (
              <>
                <div className="space-y-2">
                  <Label>Nume Dispozitiv *</Label>
                  <Input value={linkForm.device_name} onChange={e => setLinkForm(p => ({ ...p, device_name: e.target.value }))} placeholder="ex: TV Hala 1" data-testid="link-device-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Profil Kiosk</Label>
                  <Select value={linkForm.profile_id || 'none'} onValueChange={v => setLinkForm(p => ({ ...p, profile_id: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="link-profile-select"><SelectValue placeholder="Fara profil" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Fara profil (afiseaza tot)</SelectItem>
                      {profiles.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Fabrica</Label>
                  <Select value={linkForm.plant_id || 'all'} onValueChange={v => setLinkForm(p => ({ ...p, plant_id: v === 'all' ? '' : v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toate fabricile</SelectItem>
                      {(plants || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-green-600"><CheckCircle className="w-5 h-5" /><span className="font-medium">Link generat!</span></div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-white border rounded px-2 py-1 flex-1 truncate" data-testid="generated-kiosk-link">{generatedLink}</code>
                    <Button size="icon" variant="outline" className="h-8 w-8 flex-shrink-0" onClick={() => copyToClipboard(generatedLink)} data-testid="copy-link-btn"><Copy className="w-4 h-4" /></Button>
                  </div>
                </div>
                <p className="text-xs text-slate-500">Setati acest link ca pagina de start pe TV.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkDialog(false)}>{generatedLink ? 'Inchide' : 'Anuleaza'}</Button>
            {!generatedLink && (
              <Button onClick={handleGenerateLink} disabled={generating || !linkForm.device_name} data-testid="confirm-generate-link-btn">
                {generating ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />} Genereaza
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── APPROVE DEVICE DIALOG ────────── */}
      <Dialog open={showApproveDialog} onOpenChange={(open) => { if (!open) { setShowApproveDialog(false); setApprovingDevice(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-600" /> Aproba Dispozitiv Kiosk
            </DialogTitle>
          </DialogHeader>
          {approvingDevice && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-xs text-slate-500 mb-1">Cod Dispozitiv</p>
                <p className="text-2xl font-mono font-bold text-green-700 tracking-widest">{approvingDevice.device_code}</p>
                <p className="text-xs text-slate-400 mt-1">
                  Inregistrat: {approvingDevice.created_at ? new Date(approvingDevice.created_at).toLocaleString('ro-RO') : '-'}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Nume Dispozitiv</Label>
                <Input value={approveForm.device_name} onChange={e => setApproveForm(p => ({ ...p, device_name: e.target.value }))} placeholder="ex: TV Hala Productie 1" data-testid="approve-device-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Profil Kiosk</Label>
                <Select value={approveForm.profile_id || 'none'} onValueChange={v => setApproveForm(p => ({ ...p, profile_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger data-testid="approve-profile-select">
                    <SelectValue placeholder="Selecteaza profil" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Fara profil (afiseaza tot)</SelectItem>
                    {profiles.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fabrica (optional)</Label>
                <Select value={approveForm.plant_id || 'all'} onValueChange={v => setApproveForm(p => ({ ...p, plant_id: v === 'all' ? '' : v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toate fabricile</SelectItem>
                    {(plants || []).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowApproveDialog(false); setApprovingDevice(null); }}>Anuleaza</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleApproveDevice} disabled={approving} data-testid="confirm-approve-btn">
              {approving ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
              Autorizeaza
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── QR SCANNER DIALOG ────────────── */}
      <Dialog open={showQRScanner} onOpenChange={(open) => { if (!open) handleCloseQRScanner(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-green-600" /> Scaneaza Cod QR Dispozitiv
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div id="kiosk-qr-reader" ref={qrReaderRef} className="w-full min-h-[280px] bg-black rounded-lg overflow-hidden" />
            {!scanning && (
              <div className="text-center text-sm text-slate-500">
                Se porneste camera...
              </div>
            )}
            <div className="border-t pt-4">
              <p className="text-xs text-slate-500 mb-2 text-center">Sau introduceti codul manual:</p>
              <div className="flex gap-2">
                <Input
                  value={manualCode}
                  onChange={e => setManualCode(e.target.value.toUpperCase())}
                  placeholder="Ex: A1B2C3"
                  className="font-mono tracking-widest text-center"
                  maxLength={10}
                  data-testid="manual-device-code-input"
                />
                <Button onClick={() => lookupDeviceByCode(manualCode)} disabled={!manualCode || manualCode.length < 4} data-testid="manual-lookup-btn">
                  <QrCode className="w-4 h-4 mr-1" /> Cauta
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseQRScanner}>Inchide</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KioskDevicesPage;
